import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useWorkspace } from '../../../contexts/WorkspaceContext'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import {
    toggleCompleteSaga,
    updateTaskSaga
} from '../../../store/tasks/tasksSlice'
import { selectAllTasks, selectTasksLoading } from '../../../store/tasks/tasksSelectors'
import { selectAllProjects, selectProjectsLoading } from '../../../store/projects/projectsSelectors'
import { selectMembersInitialized } from '../../../store/workspace/workspaceSelectors'
import { TextH2, TextNorm } from '../../ui/Text'
import Avatar from '../../ui/Avatar/Avatar'
import Section from '../../app/Section/Section'
import PlateLoader from '../../ui/Loader/PlateLoader'
import Empty from '../../ui/Empty/Empty'
import css from './WorkspaceTeamMemberPage.module.css'

const WorkspaceTeamMemberPage = () => {
    const dispatch = useAppDispatch()
    const { workspace: workspaceUrlId, userId } = useParams<{ workspace: string; userId: string }>()
    const { workspace, loading, error, getMember } = useWorkspace()

    const tasks = useAppSelector(selectAllTasks)
    const projects = useAppSelector(selectAllProjects)
    const tasksLoading = useAppSelector(selectTasksLoading)
    const projectsLoading = useAppSelector(selectProjectsLoading)
    const membersInitialized = useAppSelector(selectMembersInitialized)

    const member = userId ? getMember(userId) : null

    const tasksByProject = useMemo(() => {
        const grouped: Record<string, typeof tasks> = {}
        tasks.forEach((task: any) => {
            if (task.assigneeId !== userId) return

            if (!grouped[task.projectId]) {
                grouped[task.projectId] = []
            }
            grouped[task.projectId].push(task)
        })
        return grouped
    }, [tasks, userId])

    const getProject = (projectId: string) => {
        return projects.find((p: any) => p.id === projectId)
    }

    const handleTaskToggleComplete = (taskId: string, isCompleted: boolean) => {
        dispatch(toggleCompleteSaga({ taskId, isCompleted }))
    }

    const handleTaskAssign = (taskId: string, assigneeId: string | null) => {
        dispatch(updateTaskSaga({ taskId, data: { assigneeId } }))
    }

    // Members load concurrently and don't gate the global loader, so `member` is
    // transiently undefined right after entry — wait for the members snapshot
    // (membersInitialized) before concluding "not found", never flash "No data".
    if (loading || tasksLoading || projectsLoading || !membersInitialized) {
        return <PlateLoader fullscreen />
    }

    if (error) {
        return <div className={css.container}>{error}</div>
    }

    if (!workspace || !member) {
        return <Empty title="Member" text="This member isn't part of the workspace." />
    }

    const projectEntries = Object.entries(tasksByProject)
        .sort(([a], [b]) => (getProject(a)?.name || '').localeCompare(getProject(b)?.name || ''))

    return (
        <div className={css.container}>
            <div className={css.header}>
                <Avatar
                    title={member.username}
                    color={member.color}
                    avatar={member.avatarImage}
                    size="medium"
                />
                <TextH2>{member.username}</TextH2>
            </div>
            {projectEntries.length === 0 ? (
                <div className={css.empty}><TextNorm>No tasks</TextNorm></div>
            ) : (
                <div className={css.lists}>
                    {projectEntries.map(([projectId, tasks]) => {
                        const project = getProject(projectId)
                        return (
                            <Section
                                key={projectId}
                                title={project?.name || 'Untitled'}
                                titleHref={`/${workspaceUrlId}/projects/${projectId}`}
                                tasks={tasks}
                                onTaskToggleComplete={handleTaskToggleComplete}
                                onTaskAssign={handleTaskAssign}
                                showCompletedTasks={true}
                                completedSortBy="recent"
                            />
                        )
                    })}
                </div>
            )}
        </div>
    )
}

export default WorkspaceTeamMemberPage
